import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import * as React from "react";

function detachLocalScreenShareVideo(localParticipant) {
  if (!localParticipant?.isScreenShareEnabled) {
    return;
  }

  const publication = localParticipant.getTrackPublication(Track.Source.ScreenShare);
  const mediaTrack = publication?.videoTrack?.mediaStreamTrack ?? publication?.track?.mediaStreamTrack;
  publication?.videoTrack?.detach?.();
  publication?.track?.detach?.();

  if (!mediaTrack) {
    return;
  }

  document.querySelectorAll("video").forEach((element) => {
    const stream = element.srcObject;
    if (!(stream instanceof MediaStream)) {
      return;
    }
    const usesScreenShare = stream
      .getVideoTracks()
      .some((track) => track.id === mediaTrack.id || track.label === mediaTrack.label);
    if (usesScreenShare) {
      element.srcObject = null;
      element.removeAttribute("src");
    }
  });
}

/**
 * While the local user is screen sharing, detach the screen-share video track from
 * every DOM element so getDisplayMedia does not capture a live feedback loop.
 */
export function LocalScreenSharePresentationGuard() {
  const { localParticipant, isScreenShareEnabled } = useLocalParticipant();

  React.useEffect(() => {
    if (!localParticipant || !isScreenShareEnabled) {
      return undefined;
    }

    const detach = () => detachLocalScreenShareVideo(localParticipant);

    detach();
    localParticipant.on("localTrackPublished", detach);
    localParticipant.on("trackPublished", detach);

    const intervalId = window.setInterval(detach, 250);
    const stopBurstId = window.setTimeout(() => window.clearInterval(intervalId), 12000);

    return () => {
      localParticipant.off("localTrackPublished", detach);
      localParticipant.off("trackPublished", detach);
      window.clearInterval(intervalId);
      window.clearTimeout(stopBurstId);
    };
  }, [isScreenShareEnabled, localParticipant]);

  return null;
}
